/* ==========================================================================
   KaryaGold — Stores Drawer controller
   Custom element <kg-stores-drawer> + wiring for [data-kg-stores-toggle].
   ========================================================================== */

(function () {
  'use strict';

  class KgStoresDrawer extends HTMLElement {
    constructor() {
      super();
      this.activeClass = 'active';
      this.bodyOpenClass = 'kg-stores-drawer-open';
      this.onOverlayClick = this.onOverlayClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onHostClick = this.onHostClick.bind(this);
      this.onKeyDown = this.onKeyDown.bind(this);
    }

    connectedCallback() {
      // Portal to <body> so the drawer is never trapped inside a parent
      // stacking context (header section uses transforms for sticky-hide
      // animations, which would clip a position:fixed child).
      if (!this._portaled && this.parentElement !== document.body) {
        this._portaled = true;
        document.body.appendChild(this);
        return; // connectedCallback re-runs after the move
      }

      this.overlay = this.querySelector('[data-kg-stores-overlay]');
      this.closeBtn = this.querySelector('[data-kg-stores-close]');
      this.inner = this.querySelector('.kg-stores-drawer__inner');

      if (this.overlay) this.overlay.addEventListener('click', this.onOverlayClick);
      if (this.closeBtn) this.closeBtn.addEventListener('click', this.onCloseClick);
      // Catch any click that lands on the host but outside the panel —
      // belt-and-suspenders so the drawer always closes on outside click.
      this.addEventListener('click', this.onHostClick);

      // Toggles can live elsewhere in the DOM (header). Wire any present now.
      this.bindToggles();
    }

    disconnectedCallback() {
      if (this.overlay) this.overlay.removeEventListener('click', this.onOverlayClick);
      if (this.closeBtn) this.closeBtn.removeEventListener('click', this.onCloseClick);
      this.removeEventListener('click', this.onHostClick);
      document.removeEventListener('keydown', this.onKeyDown);
    }

    bindToggles() {
      const toggles = document.querySelectorAll('[data-kg-stores-toggle]');
      toggles.forEach((btn) => {
        if (btn._kgStoresBound) return;
        btn._kgStoresBound = true;
        btn.addEventListener('click', (e) => {
          e.preventDefault();
          this.open(btn);
        });
      });
    }

    open(trigger) {
      this.classList.add(this.activeClass);
      this.setAttribute('aria-hidden', 'false');
      document.body.classList.add(this.bodyOpenClass);
      document.addEventListener('keydown', this.onKeyDown);
      this._lastTrigger = trigger || null;
      if (trigger) trigger.setAttribute('aria-expanded', 'true');

      // Focus the panel for a11y once the slide-in is done.
      window.setTimeout(() => {
        if (this.inner) this.inner.focus({ preventScroll: true });
      }, 200);
    }

    close() {
      this.classList.remove(this.activeClass);
      this.setAttribute('aria-hidden', 'true');
      document.body.classList.remove(this.bodyOpenClass);
      document.removeEventListener('keydown', this.onKeyDown);

      const toggles = document.querySelectorAll('[data-kg-stores-toggle]');
      toggles.forEach((btn) => btn.setAttribute('aria-expanded', 'false'));

      if (this._lastTrigger && typeof this._lastTrigger.focus === 'function') {
        this._lastTrigger.focus({ preventScroll: true });
      }
      this._lastTrigger = null;
    }

    onOverlayClick() {
      this.close();
    }

    onCloseClick() {
      this.close();
    }

    onHostClick(e) {
      // Close only when the click is outside the sliding panel.
      if (!this.classList.contains(this.activeClass)) return;
      if (!this.inner) return;
      if (e.target === this.inner || this.inner.contains(e.target)) return;
      this.close();
    }

    onKeyDown(e) {
      if (e.key === 'Escape' || e.key === 'Esc') {
        e.preventDefault();
        this.close();
      }
    }
  }

  if (!customElements.get('kg-stores-drawer')) {
    customElements.define('kg-stores-drawer', KgStoresDrawer);
  }

  // Fallback: if the drawer element loads after toggles were clicked,
  // re-bind on DOMContentLoaded.
  document.addEventListener('DOMContentLoaded', () => {
    const drawer = document.querySelector('kg-stores-drawer');
    if (drawer && typeof drawer.bindToggles === 'function') drawer.bindToggles();
  });
})();
